import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Typography, Spin, Button, Alert, Space, Tag } from 'antd';
import { ClockCircleOutlined, EnvironmentOutlined, CarOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  supabase,
  TripMapView,
  GpsTrackHistoryList,
  parseGeoPoint,
  formatETA,
  formatDistance,
  resolveTripMapPathDisplay,
  locationLogsToTrackPoints,
  buildTripMapMarkers,
  type LocationLog,
  type Trip,
  type LatLng,
  type TrackPoint,
  type TripMapViewHandle,
  type RouteResult,
} from '@ubus/shared';
import { translateClientTripStatus } from '../utils/clientStatusLabels';

const { Title, Text } = Typography;

function LiveMap() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const mapRef = useRef<TripMapViewHandle>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const tripRef = useRef<Trip | null>(null);
  const vehiclePositionRef = useRef<LatLng | null>(null);

  const [loading, setLoading] = useState(true);
  const [mapError, setMapError] = useState<string | null>(null);
  const [routePlanningError, setRoutePlanningError] = useState<string | null>(null);
  const [trip, setTrip] = useState<Trip | null>(null);
  const [locationLogs, setLocationLogs] = useState<LocationLog[]>([]);
  const [vehiclePosition, setVehiclePosition] = useState<LatLng | null>(null);
  const [eta, setEta] = useState<RouteResult | null>(null);
  const [etaLoading, setEtaLoading] = useState(false);
  const [lastKnownAddress, setLastKnownAddress] = useState('');
  const [lastUpdate, setLastUpdate] = useState<string | null>(null);

  const pickup = trip?.pickup_geopoint ? parseGeoPoint(trip.pickup_geopoint) : null;
  const dropoff = trip?.dropoff_geopoint ? parseGeoPoint(trip.dropoff_geopoint) : null;
  const center = pickup ?? dropoff ?? { lat: -33.8688, lng: 151.2093 };

  const trackPoints = useMemo(() => locationLogsToTrackPoints(locationLogs), [locationLogs]);

  const pathDisplay = useMemo(
    () => resolveTripMapPathDisplay(trackPoints, pickup, dropoff),
    [trackPoints, pickup, dropoff]
  );

  const markers = useMemo(
    () =>
      buildTripMapMarkers({
        pickup,
        dropoff,
        vehicle: vehiclePosition,
        trackPoints,
        showTrackPoints: pathDisplay.mode === 'track',
      }),
    [pickup, dropoff, vehiclePosition, trackPoints, pathDisplay.mode]
  );

  const fetchTrip = useCallback(async () => {
    if (!id) return null;
    const { data, error } = await supabase
      .from('trips')
      .select('*, vehicles(*), drivers(*)')
      .eq('id', id)
      .single();

    if (error || !data) return null;
    setTrip(data);
    tripRef.current = data;
    return data;
  }, [id]);

  const refreshEta = useCallback(async (from: LatLng, destination: string | LatLng) => {
    if (!mapRef.current) return;
    setEtaLoading(true);
    try {
      const result = await mapRef.current.getDrivingRoute(from, destination);
      if (result) setEta(result);
    } finally {
      setEtaLoading(false);
    }
  }, []);

  const applyVehiclePosition = useCallback(async (position: LatLng, tripData: Trip) => {
    setVehiclePosition(position);
    mapRef.current?.updateMarker('vehicle', position);
    mapRef.current?.panTo(position);
    setLastUpdate(new Date().toISOString());

    const address = await mapRef.current?.reverseGeocode(position);
    if (address) setLastKnownAddress(address);

    if (tripData.status === 'in_progress' && tripData.dropoff_address) {
      await refreshEta(position, tripData.dropoff_address);
    }
  }, [refreshEta]);

  const loadLocationHistory = useCallback(async () => {
    if (!id) return;
    const { data: logs } = await supabase
      .from('location_logs')
      .select('*')
      .eq('trip_id', id)
      .order('timestamp', { ascending: true });

    const rows = (logs ?? []) as LocationLog[];
    setLocationLogs(rows);

    const latest = rows[rows.length - 1];
    if (latest?.geopoint && tripRef.current) {
      const parsed = parseGeoPoint(latest.geopoint);
      if (parsed) await applyVehiclePosition(parsed, tripRef.current);
    }
  }, [id, applyVehiclePosition]);

  const subscribeToLocationUpdates = useCallback((tripData: Trip) => {
    if (!id) return;
    if (channelRef.current) supabase.removeChannel(channelRef.current);

    channelRef.current = supabase
      .channel(`location_logs_live_${id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'location_logs',
          filter: `trip_id=eq.${id}`,
        },
        (payload) => {
          const newLog = payload.new as LocationLog;
          setLocationLogs((prev) => [...prev, newLog]);
          const parsed = newLog.geopoint ? parseGeoPoint(newLog.geopoint) : null;
          if (parsed) applyVehiclePosition(parsed, tripData);
        }
      )
      .subscribe();
  }, [id, applyVehiclePosition]);

  useEffect(() => {
    if (trip) tripRef.current = trip;
  }, [trip]);

  useEffect(() => {
    vehiclePositionRef.current = vehiclePosition;
  }, [vehiclePosition]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const tripData = await fetchTrip();
      setLoading(false);
      if (!tripData) setMapError(t('map.tripNotFound'));
    })();
  }, [fetchTrip, t]);

  useEffect(() => {
    return () => {
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  }, []);

  useEffect(() => {
    if (!vehiclePosition || !trip || trip.status !== 'in_progress') return;

    const intervalId = window.setInterval(() => {
      const pos = vehiclePositionRef.current;
      const currentTrip = tripRef.current;
      if (!pos || !currentTrip?.dropoff_address) return;
      void refreshEta(pos, currentTrip.dropoff_address);
    }, 120_000);

    return () => window.clearInterval(intervalId);
  }, [vehiclePosition, trip?.status, refreshEta]);

  const handleMapReady = async () => {
    const tripData = tripRef.current ?? (await fetchTrip());
    if (!tripData) return;
    await loadLocationHistory();
    subscribeToLocationUpdates(tripData);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>{t('map.loading')}</div>
      </div>
    );
  }

  if (mapError || !trip) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={3} style={{ margin: 0 }}>{t('map.title')}</Title>
          <Button onClick={() => navigate('/dashboard')}>{t('map.back')}</Button>
        </div>
        <Card>
          <Alert type="warning" message={mapError || t('map.mapUnavailable')} showIcon />
        </Card>
      </div>
    );
  }

  const tripStatusLabel = translateClientTripStatus(t, trip);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>{t('map.title')}</Title>
        <Button onClick={() => navigate('/dashboard')}>{t('map.back')}</Button>
      </div>

      <Card>
        {routePlanningError && pathDisplay.mode === 'planned' ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('map.routePlanningFailed')}
            description={routePlanningError}
          />
        ) : null}

        <div style={{ width: '100%', height: '70vh', borderRadius: 8, overflow: 'hidden' }}>
          {mapError ? (
            <div style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 32,
              background: '#fafafa',
            }}>
              <Alert
                type="warning"
                message={t('map.mapUnavailable')}
                description={mapError}
                showIcon
                style={{ marginBottom: 24, maxWidth: 480 }}
              />
              <Space direction="vertical" size="middle" style={{ textAlign: 'center' }}>
                <div>
                  <EnvironmentOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                  <Text strong>{t('common.pickup')}: </Text>
                  <Text>{trip.pickup_address}</Text>
                </div>
                {trip.drivers?.phone && (
                  <div>
                    <Text strong>{t('map.driverPhone')} </Text>
                    <Text copyable>{trip.drivers.phone}</Text>
                  </div>
                )}
              </Space>
            </div>
          ) : (
            <TripMapView
              ref={mapRef}
              center={center}
              zoom={13}
              markers={markers}
              track={pathDisplay.track}
              route={pathDisplay.route}
              onReady={handleMapReady}
              onError={(msg) => setMapError(msg)}
              onRoutePlanningError={(message) => setRoutePlanningError(message)}
              onRoutePlanningSuccess={() => setRoutePlanningError(null)}
            />
          )}
        </div>

        {pathDisplay.mode === 'track' && trackPoints.length > 0 ? (
          <GpsTrackHistoryList
            points={trackPoints}
            onSelectPoint={(point: TrackPoint) => mapRef.current?.panTo(point)}
          />
        ) : null}

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <Tag color={trip.status === 'in_progress' ? 'processing' : 'blue'}>
              {tripStatusLabel}
            </Tag>

            {eta && (
              <div style={{
                background: 'linear-gradient(135deg, #FA8C16 0%, #FFA940 100%)',
                color: 'white',
                padding: '10px 20px',
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
                <ClockCircleOutlined />
                <span style={{ fontWeight: 600 }}>
                  {t('map.etaToArrival', { eta: formatETA(eta.durationSeconds) })}
                </span>
                <span style={{ opacity: 0.85, fontSize: 13 }}>
                  ({formatDistance(eta.distanceMeters)})
                </span>
              </div>
            )}

            {etaLoading && !eta && <Spin size="small" />}

            {lastKnownAddress && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <CarOutlined style={{ color: '#fa8c16' }} />
                <Text type="secondary">{t('map.vehicleLocation', { address: lastKnownAddress })}</Text>
              </div>
            )}
          </div>

          {lastUpdate && (
            <Text type="secondary">
              {t('map.lastUpdate', { time: new Date(lastUpdate).toLocaleTimeString() })}
            </Text>
          )}
        </div>

        <Space direction="vertical" size="small" style={{ marginTop: 12, width: '100%' }}>
          <div>
            <EnvironmentOutlined style={{ marginRight: 8, color: '#52c41a' }} />
            <Text strong>{t('common.pickup')}: </Text>
            <Text>{trip.pickup_address}</Text>
          </div>
          <div>
            <EnvironmentOutlined style={{ marginRight: 8, color: '#ff4d4f' }} />
            <Text strong>{t('common.dropoff')}: </Text>
            <Text>{trip.dropoff_address}</Text>
          </div>
        </Space>
      </Card>
    </div>
  );
}

export default LiveMap;
